import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Fab,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add as AddIcon, ViewModule, ViewList } from '@mui/icons-material';
import { getMolds, getMoldsByCustomerId } from '../services/mold.service';
import { lifeCycleService } from '../services/lifeCycle.service';
import { productionLogService } from '../services/production-log.service';
import { moldHistoryService } from '../services/mold-history.service';
import { transactionService } from '../services/transaction.service';
import { customerService } from '../services/customer.service';
import { HistoryType } from '../types/history.types';
import { Mold, MoldStatus, MoldStatusMap, Customer } from '../types';
import { ProductionLogModal } from '../components/ProductionLogModal';
import { useAuth } from '../context/AuthContext';

const Glyph = ({ icon, size = '1.2rem' }: { icon: string; size?: string }) => (
  <Box
    component="span"
    className="material-symbols-rounded"
    sx={{
      fontSize: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    }}
  >
    {icon}
  </Box>
);

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

// 출고 작업 유형 라벨 (Transaction.tsx와 동일)
const WORK_TYPE_LABELS: Partial<Record<HistoryType, string>> = {
  production: '생산',
  trial_production: '시사출',
  maintenance: '정비',
  repair: '수리',
  inspection: '점검',
  modification: '개조',
};

const MoldList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const [molds, setMolds] = useState<Mold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<MoldStatus | 'all'>('all');
  const [needCheckFilter, setNeedCheckFilter] = useState(false);
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'life-high' | 'life-low' | 'moldNumber-asc' | 'moldNumber-desc'>('newest');
  const [viewType, setViewType] = useState<'card' | 'list'>('card');

  // 업체(고객사) 필터
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  
  // 생산량 입력 모달 상태
  const [prodLogModalOpen, setProdLogModalOpen] = useState(false);
  const [selectedMold, setSelectedMold] = useState<Mold | null>(null);

  // 사용중 금형의 누적 생산량 (moldId -> 생산량 합계)
  const [productionTotals, setProductionTotals] = useState<Map<string, number>>(new Map());
  
  // 사용중 금형의 출고 정보 (moldId -> {workType, outboundDate})
  const [outboundInfo, setOutboundInfo] = useState<Map<string, {workType: string, outboundDate: Date}>>(new Map());
  
  // 사용중 금형의 출고 후 생산량 (moldId -> 생산량 합계)
  const [postOutboundProduction, setPostOutboundProduction] = useState<Map<string, number>>(new Map());

  // 고객사/협력사 여부 확인
  const isCustomerUser = user?.role === 'customer' || user?.role === 'partner';

  // 초기 필터 설정 (Dashboard에서 전달받은 경우)
  useEffect(() => {
    const state = location.state as { filter?: MoldStatus | 'all' | 'needCheck' };
    if (state?.filter) {
      if (state.filter === 'needCheck') {
        setStatusFilter('all');
        setNeedCheckFilter(true);
      } else {
        setStatusFilter(state.filter);
        setNeedCheckFilter(false);
      }
    }
  }, [location]);

  const loadMolds = async () => {
    try {
      setLoading(true);
      
      // 고객사/협력사는 자사 금형만 조회
      let moldData: Mold[];
      if (isCustomerUser && user?.customerId) {
        moldData = await getMoldsByCustomerId(user.customerId);
      } else {
        moldData = await getMolds();
      }
      
      setMolds(moldData);

      // 사용중 금형의 생산 로그 및 출고 정보 조회
      const inUseMolds = moldData.filter(m => m.status === 'in-use');
      const totalsMap = new Map<string, number>();
      const outboundMap = new Map<string, {workType: string, outboundDate: Date}>();
      const postOutboundMap = new Map<string, number>();
      
      await Promise.all(
        inUseMolds.map(async (mold) => {
          if (mold.id) {
            try {
              // 1. 전체 생산 로그 합계
              const logs = await productionLogService.getByMoldId(mold.id);
              const total = productionLogService.calculateTotal(logs);
              totalsMap.set(mold.id, total);
              
              // 2. 최근 출고 정보 조회 (transactions 컬렉션에서 check-out)
              const transactions = await transactionService.getTransactionHistory(mold.id);
              const checkOutTransactions = transactions
                .filter(t => t.type === 'check-out')
                .sort((a, b) => b.timestamp.toDate().getTime() - a.timestamp.toDate().getTime());
              
              if (checkOutTransactions.length > 0) {
                const latestCheckOut = checkOutTransactions[0];
                const purposeKey = latestCheckOut.purpose || 'production';
                // purpose를 한글로 변환 (trial_production -> 시사출, production -> 생산 등)
                const workType = WORK_TYPE_LABELS[purposeKey as HistoryType] || purposeKey;
                const outboundDate = latestCheckOut.timestamp.toDate();
                // 날짜만 비교하도록 시간을 00:00:00으로 설정 (같은 날 생산량 포함)
                outboundDate.setHours(0, 0, 0, 0);
                
                outboundMap.set(mold.id, {
                  workType,
                  outboundDate
                });
                
                // 3. 출고일 이후 생산량 합계 (mold_histories에서 production + trial_production)
                const histories = await moldHistoryService.getByMoldId(mold.id);
                
                const productionHistories = histories.filter(h => 
                  (h.type === 'production' || h.type === 'trial_production') &&
                  h.date >= outboundDate
                );
                
                const postOutboundTotal = productionHistories.reduce((sum, h) => 
                  sum + (h.productionQty || h.shots || 0), 0
                );
                
                postOutboundMap.set(mold.id, postOutboundTotal);
              }
            } catch (error) {
              console.error(`생산 로그 조회 실패 (${mold.moldNumber}):`, error);
              totalsMap.set(mold.id, 0);
            }
          }
        })
      );
      
      setProductionTotals(totalsMap);
      setOutboundInfo(outboundMap);
      setPostOutboundProduction(postOutboundMap);
      setError(null);
    } catch (err) {
      setError('금형 데이터를 불러오는 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 생산량 입력 모달 열기
  const handleOpenProdLog = (mold: Mold, e: React.MouseEvent) => {
    e.stopPropagation(); // 카드 클릭 이벤트 방지
    setSelectedMold(mold);
    setProdLogModalOpen(true);
  };

  // 생산량 입력 완료 후
  const handleProdLogSuccess = () => {
    loadMolds(); // 금형 목록 새로고침
  };


  const getLifePercentage = (currentShots: number, designLife: number) =>
    Math.min(100, Math.round((currentShots / designLife) * 100));

  // 수명 표시 포맷 (사용중이면 누적 생산량 포함하여 괄호 표시)
  const formatLifeDisplay = (mold: Mold) => {
    const design = formatNumber(mold.designLife);
    
    if (mold.status === 'in-use') {
      const postOutboundTotal = mold.id ? (postOutboundProduction.get(mold.id) || 0) : 0;
      const estimatedTotal = mold.currentShots + postOutboundTotal;
      return `(${formatNumber(estimatedTotal)})/${design}`;
    }
    return `${formatNumber(mold.currentShots)}/${design}`;
  };

  const getStatusStyle = (status: MoldStatus) => {
    switch (status) {
      case 'stored':
        return { 
          bgcolor: '#4caf50', // 초록
          color: '#fff',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      case 'in-use':
        return { 
          bgcolor: '#fdd835', // 노랑
          color: '#000',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      case 'maintenance':
        return { 
          bgcolor: '#f44336', // 빨강
          color: '#fff',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      case 'disposed':
        return { 
          bgcolor: '#9e9e9e', // 회색
          color: '#fff',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
      default:
        return { 
          bgcolor: 'default',
          color: 'inherit',
          fontWeight: 600,
          backgroundImage: 'none', // 그라데이션 제거
        };
    }
  };

  const statusCounts = useMemo(() => ({
    total: molds.length,
    stored: molds.filter((m) => m.status === 'stored').length,
    'in-use': molds.filter((m) => m.status === 'in-use').length,
    maintenance: molds.filter((m) => m.status === 'maintenance').length,
    disposed: molds.filter((m) => m.status === 'disposed').length,
  }), [molds]);

  const filteredSummary = useMemo(
    () => ({
      total: molds.length,
      inUse: molds.filter((mold) => mold.status === 'in-use').length,
      maintenance: molds.filter((mold) => mold.status === 'maintenance').length,
    }),
    [molds],
  );

  const filteredMolds = useMemo(() => {
    let result = molds;
    
    // 검색어 필터링 (실시간, 대소문자 무시)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(m => 
        m.moldNumber.toLowerCase().includes(term) ||
        m.moldName.toLowerCase().includes(term) ||
        (m.customerName?.toLowerCase() || '').includes(term)
      );
    }
    
    // 업체(고객사) 필터링
    if (customerFilter !== 'all') {
      result = result.filter(m => m.customerId === customerFilter);
    }
    
    // 상태 필터링
    if (statusFilter !== 'all') {
      result = result.filter(m => m.status === statusFilter);
    }
    
    // 점검 필요 필터링
    if (needCheckFilter) {
      result = result.filter(m => lifeCycleService.shouldTriggerAlert(m));
    }
    
    // 정렬 적용
    result = [...result].sort((a, b) => {
      switch (sortOption) {
        case 'newest':
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        case 'oldest':
          return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
        case 'life-high':
          return (b.currentShots / b.designLife) - (a.currentShots / a.designLife);
        case 'life-low':
          return (a.currentShots / a.designLife) - (b.currentShots / b.designLife);
        case 'moldNumber-asc':
          return a.moldNumber.localeCompare(b.moldNumber, 'ko-KR', { numeric: true });
        case 'moldNumber-desc':
          return b.moldNumber.localeCompare(a.moldNumber, 'ko-KR', { numeric: true });
        default:
          return 0;
      }
    });
    
    return result;
  }, [molds, searchTerm, customerFilter, statusFilter, needCheckFilter, sortOption]);

  useEffect(() => {
    loadMolds();
  }, []);

  // 고객사 목록 로드
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await customerService.getAll();
        setCustomers(data);
      } catch (err) {
        console.error('고객사 목록을 불러오지 못했습니다:', err);
      }
    };
    
    // 관리자/매니저만 고객사 필터 사용
    if (!isCustomerUser) {
      loadCustomers();
    }
  }, [isCustomerUser]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 }, p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            금형 대장
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            금형별 상태와 라이프사이클을 빠르게 조회하고 상세 정보로 이동하세요.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={<Glyph icon="dataset" size="1.05rem" />}
            label={`${statusFilter === 'all' ? '전체' : MoldStatusMap[statusFilter as MoldStatus]} ${formatNumber(filteredMolds.length)}건`}
            color="secondary"
            sx={{ 
              fontWeight: 600, 
              letterSpacing: '0.08em', 
              textTransform: 'uppercase',
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
          <Chip
            icon={<Glyph icon="precision_manufacturing" />}
            label={`사용 중 ${formatNumber(filteredSummary.inUse)}대`}
            variant="outlined"
            sx={{
              borderRadius: 999,
              borderColor: alpha(theme.palette.primary.light, 0.35),
              color: theme.palette.text.primary,
              fontWeight: 500,
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
          <Chip
            icon={<Glyph icon="build" />}
            label={`점검 중 ${formatNumber(filteredSummary.maintenance)}대`}
            variant="outlined"
            sx={{
              borderRadius: 999,
              borderColor: alpha(theme.palette.warning.main, 0.4),
              color: theme.palette.text.primary,
              fontWeight: 500,
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        </Stack>
      </Stack>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(140deg, ${alpha(theme.palette.primary.dark, 0.28)}, ${alpha(
            theme.palette.background.paper,
            0.92,
          )})`,
          boxShadow: `0 16px 32px ${alpha(theme.palette.primary.dark, 0.24)}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              fullWidth
              placeholder="금형 번호, 금형명, 고객사명 검색 (실시간)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="medium"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* 상태 필터 버튼 */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant={statusFilter === 'all' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFilter('all')}
            sx={{ borderRadius: 2, fontWeight: 600, px: 1.5 }}
          >
            전체 ({statusCounts.total})
          </Button>
          <Button
            variant={statusFilter === 'stored' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFilter('stored')}
            sx={{ borderRadius: 2, fontWeight: 600, px: 1.5 }}
          >
            보관중 ({statusCounts.stored})
          </Button>
          <Button
            variant={statusFilter === 'in-use' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFilter('in-use')}
            sx={{ borderRadius: 2, fontWeight: 600, px: 1.5 }}
          >
            사용중 ({statusCounts['in-use']})
          </Button>
          <Button
            variant={statusFilter === 'maintenance' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFilter('maintenance')}
            sx={{ borderRadius: 2, fontWeight: 600, px: 1.5 }}
          >
            점검중 ({statusCounts.maintenance})
          </Button>
          <Button
            variant={statusFilter === 'disposed' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFilter('disposed')}
            sx={{ borderRadius: 2, fontWeight: 600, px: 1.5 }}
          >
            폐기 ({statusCounts.disposed})
          </Button>
        </Stack>

        {/* 업체 필터 & 정렬 드롭다운 */}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* 업체 필터 (관리자/매니저만) */}
          {!isCustomerUser && customers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
                displayEmpty
              >
                <MenuItem value="all">전체 업체</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as any)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="newest">최신순</MenuItem>
              <MenuItem value="oldest">오래된순</MenuItem>
              <MenuItem value="life-high">수명높은순</MenuItem>
              <MenuItem value="life-low">수명낮은순</MenuItem>
              <MenuItem value="moldNumber-asc">금형번호(오름차순)</MenuItem>
              <MenuItem value="moldNumber-desc">금형번호(내림차순)</MenuItem>
            </Select>
          </FormControl>

          {/* 뷰타입 토글 */}
          <Stack direction="row" spacing={0.5}>
            <Button
              variant={viewType === 'card' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewType('card')}
              sx={{ minWidth: 40, px: 0.75 }}
            >
              <ViewModule fontSize="small" />
            </Button>
            <Button
              variant={viewType === 'list' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewType('list')}
              sx={{ minWidth: 40, px: 0.75 }}
            >
              <ViewList fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            금형 목록을 불러오는 중...
          </Typography>
        </Box>
      ) : filteredMolds.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            textAlign: 'center',
            borderRadius: 3,
            border: `1px dashed ${alpha(theme.palette.primary.light, 0.35)}`,
            backgroundColor: alpha(theme.palette.primary.dark, 0.18),
            py: { xs: 5, md: 6 },
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {molds.length === 0 
              ? '현재 등록된 금형 정보가 없습니다. 우측 하단의 추가 버튼으로 금형을 등록해 주세요.'
              : '해당 조건에 맞는 금형이 없습니다.'}
          </Typography>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: viewType === 'card' 
              ? { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' }
              : '1fr',
            gap: viewType === 'list' ? 1 : { xs: 1.5, md: 2 },
          }}
        >
          {filteredMolds.map((mold) => {
            const lifePercent = getLifePercentage(mold.currentShots, mold.designLife);
            const isCritical = lifePercent >= 90;
            const isWarning = lifePercent >= 80; // 80% 이상 경고

            return (
              <Card
                key={mold.id}
                elevation={0}
                sx={{
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: viewType === 'list' ? 2 : 3,
                  border: `1px solid ${alpha(theme.palette.primary.light, isCritical ? 0.4 : 0.22)}`,
                  backgroundImage: `linear-gradient(165deg, ${alpha(
                    theme.palette.primary.dark,
                    isCritical ? 0.4 : 0.2,
                  )}, ${alpha(theme.palette.background.paper, 0.92)})`,
                  boxShadow: viewType === 'list' 
                    ? `0 2px 8px ${alpha(theme.palette.primary.dark, 0.15)}`
                    : `0 16px 30px ${alpha(theme.palette.primary.dark, 0.2)}`,
                  transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                  ...(viewType === 'list' && { padding: '0 !important' }),
                  '&:hover': {
                    transform: viewType === 'list' ? 'translateX(2px)' : 'translateY(-4px)',
                    boxShadow: viewType === 'list'
                      ? `0 4px 12px ${alpha(theme.palette.primary.dark, 0.2)}`
                      : `0 20px 40px ${alpha(theme.palette.primary.dark, 0.28)}`,
                    borderColor: alpha(theme.palette.primary.light, 0.45),
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: viewType === 'list' ? 'row' : 'column',
                  gap: viewType === 'list' ? 0.5 : 1.5, 
                  p: viewType === 'list' ? '2px 4px' : 1.5,
                  alignItems: viewType === 'list' ? 'center' : 'stretch'
                }}>
                  {viewType === 'list' ? (
                    // 리스트형: 한 줄 레이아웃
                    <>
                      {/* 썸네일 */}
                      <Box
                        onClick={() => navigate(`/molds/${mold.id}`)}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          backgroundColor: alpha(theme.palette.primary.dark, 0.15),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          cursor: 'pointer',
                          transition: 'transform 150ms ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          }
                        }}
                      >
                        {mold.thumbnails && mold.thumbnails.length > 0 ? (
                          <Box
                            component="img"
                            src={mold.thumbnails[0]}
                            alt={mold.moldName}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : mold.images && mold.images.length > 0 ? (
                          <Box
                            component="img"
                            src={mold.images[0]}
                            alt={mold.moldName}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <Glyph icon="manufacturing" size="1.8rem" />
                        )}
                      </Box>

                      {/* 금형번호 + 상태 */}
                      <Stack sx={{ minWidth: { xs: 80, sm: 100 }, flexShrink: 0 }}>
                        <Typography 
                          variant="subtitle2"
                          onClick={() => navigate(`/molds/${mold.id}`)}
                          sx={{ 
                            fontWeight: 700,
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            cursor: 'pointer',
                            '&:hover': {
                              color: theme.palette.primary.light,
                            }
                          }}
                        >
                          {mold.moldNumber}
                        </Typography>
                        <Chip
                          label={MoldStatusMap[mold.status as MoldStatus || 'stored']}
                          size="small"
                          sx={{ 
                            ...getStatusStyle((mold.status as MoldStatus) || 'stored'),
                            fontSize: '0.65rem',
                            height: 18,
                            mt: 0.3,
                            '& .MuiChip-label': {
                              paddingLeft: '4px !important',
                              paddingRight: '4px !important'
                            }
                          }}
                        />
                      </Stack>

                      {/* 금형명 */}
                      <Stack sx={{ minWidth: { xs: 60, sm: 120 }, flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          color: 'text.primary',
                          fontWeight: 500,
                          fontSize: { xs: '0.75rem', sm: '0.8rem' }
                        }}>
                          {mold.moldName}
                        </Typography>
                      </Stack>

                      {/* 고객사 | 보관위치 - 모바일에서 숨김 */}
                      <Stack sx={{ 
                        minWidth: 120,
                        display: { xs: 'none', sm: 'flex' }
                      }}>
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.7rem'
                        }}>
                          {mold.customerName ?? '미등록'}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.68rem'
                        }}>
                          {mold.location ?? '미등록'}
                        </Typography>
                      </Stack>

                      {/* 수명 + 쇼트수 */}
                      <Stack direction="row" spacing={0.3} alignItems="center" sx={{ minWidth: 180 }}>
                        <Chip
                          icon={<Glyph icon="speed" size="0.75rem" />}
                          label={`${lifePercent}%`}
                          size="small"
                          color={isWarning ? 'warning' : 'default'}
                          variant={isWarning ? 'filled' : 'outlined'}
                          sx={{
                            borderRadius: 999,
                            ...(!isWarning && {
                              borderColor: alpha(theme.palette.primary.light, 0.35),
                              color: theme.palette.text.primary,
                            }),
                            fontWeight: 500,
                            fontSize: '0.68rem',
                            height: 20,
                            '& .MuiChip-label': {
                              paddingLeft: '4px !important',
                              paddingRight: '4px !important'
                            },
                            '& .MuiChip-icon': {
                              marginLeft: '4px',
                              marginRight: '-2px'
                            }
                          }}
                        />
                        <Chip
                          icon={<Glyph icon="counter_1" size="0.7rem" />}
                          label={formatLifeDisplay(mold)}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 999,
                            borderColor: alpha(theme.palette.info.light, 0.3),
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.62rem',
                            height: 20,
                            '& .MuiChip-label': {
                              paddingLeft: '3px !important',
                              paddingRight: '3px !important'
                            },
                            '& .MuiChip-icon': {
                              marginLeft: '3px',
                              marginRight: '-2px'
                            }
                          }}
                        />
                        
                        {/* 이번 출고후 쇼트 (사용중인 경우만) */}
                        {mold.status === 'in-use' && mold.id && postOutboundProduction.has(mold.id) && (
                          <Chip
                            icon={<Glyph icon="manufacturing" size="0.7rem" />}
                            label={`이번출고후 쇼트: ${formatNumber(postOutboundProduction.get(mold.id) || 0)}개`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 999,
                              borderColor: alpha(theme.palette.success.light, 0.3),
                              color: theme.palette.success.main,
                              fontWeight: 500,
                              fontSize: '0.62rem',
                              height: 20,
                              '& .MuiChip-label': {
                                paddingLeft: '3px !important',
                                paddingRight: '3px !important'
                              },
                              '& .MuiChip-icon': {
                                marginLeft: '3px',
                                marginRight: '-2px'
                              }
                            }}
                          />
                        )}
                        
                        {/* 상태 칩 */}
                        <Chip
                          label={
                            mold.status === 'in-use' && mold.id && outboundInfo.get(mold.id)
                              ? `${MoldStatusMap[mold.status as MoldStatus]} (${outboundInfo.get(mold.id)?.workType})`
                              : MoldStatusMap[(mold.status as MoldStatus) || 'stored']
                          }
                          size="small"
                          sx={{ 
                            ...getStatusStyle((mold.status as MoldStatus) || 'stored'),
                            fontSize: '0.62rem',
                            height: 20,
                            '& .MuiChip-label': {
                              paddingLeft: '4px !important',
                              paddingRight: '4px !important'
                            }
                          }}
                        />
                        
                        {isCritical && (
                          <Chip
                            icon={<Glyph icon="priority_high" size="0.75rem" />}
                            label="점검"
                            color="warning"
                            size="small"
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.63rem',
                              height: 20,
                              '& .MuiChip-label': {
                                paddingLeft: '4px !important',
                                paddingRight: '4px !important'
                              },
                              '& .MuiChip-icon': {
                                marginLeft: '4px',
                                marginRight: '-2px'
                              }
                            }}
                          />
                        )}
                      </Stack>

                      {/* 생산량 입력 버튼 (사용중일 때만, 관리자/일반 사용자만) - 리스트형 */}
                      {!isCustomerUser && mold.status === 'in-use' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => handleOpenProdLog(mold, e)}
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            height: 24,
                            px: 1,
                            borderRadius: 1.5,
                            borderColor: alpha(theme.palette.primary.light, 0.5),
                            '&:hover': {
                              borderColor: theme.palette.primary.light,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            }
                          }}
                        >
                          생산량
                        </Button>
                      )}
                    </>
                  ) : (
                    // 카드형: 2행 레이아웃
                    <>
                      {/* 1행: 정보영역 70% | 썸네일 30% */}
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, width: '100%' }}>
                        {/* 정보영역 70% */}
                        <Stack sx={{ flex: '0 0 70%', gap: 0.5, minWidth: 0 }}>
                          <Typography 
                            variant="h6" 
                            onClick={() => navigate(`/molds/${mold.id}`)}
                            sx={{ 
                              fontWeight: 700, 
                              letterSpacing: '-0.01em',
                              fontSize: '1.35rem',
                              cursor: 'pointer',
                              lineHeight: 1.3,
                              '&:hover': {
                                color: theme.palette.primary.light,
                              }
                            }}
                          >
                            {mold.moldNumber}
                          </Typography>
                          
                          <Typography variant="body2" sx={{ 
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            lineHeight: 1.3
                          }}>
                            {mold.moldName}
                          </Typography>
                          
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.85rem',
                            lineHeight: 1.3
                          }}>
                            {mold.customerName ?? '미등록'} | {mold.location ?? '미등록'}
                          </Typography>
                        </Stack>
                        
                        {/* 썸네일 30% */}
                        <Box
                          onClick={() => navigate(`/molds/${mold.id}`)}
                          sx={{
                            flex: '0 0 30%',
                            aspectRatio: '1',
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: alpha(theme.palette.primary.dark, 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'transform 150ms ease, box-shadow 150ms ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.dark, 0.3)}`,
                            }
                          }}
                        >
                          {mold.thumbnails && mold.thumbnails.length > 0 ? (
                            <Box
                              component="img"
                              src={mold.thumbnails[0]}
                              alt={mold.moldName}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : mold.images && mold.images.length > 0 ? (
                            <Box
                              component="img"
                              src={mold.images[0]}
                              alt={mold.moldName}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Glyph icon="manufacturing" size="2.5rem" />
                          )}
                        </Box>
                      </Box>
                      
                      {/* 2행: 칩태그 70% | 생산량버튼 30% */}
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, width: '100%', alignItems: 'flex-start' }}>
                        {/* 칩태그 70% - 1줄 정렬 (자동 줄바꿈) */}
                        <Box sx={{ flex: '0 0 70%', display: 'flex', flexDirection: 'row', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
                          {/* 수명 */}
                          <Chip
                            icon={<Glyph icon="speed" size="0.95rem" />}
                            label={`${lifePercent}%`}
                            size="small"
                            color={isWarning ? 'warning' : 'default'}
                            variant={isWarning ? 'filled' : 'outlined'}
                            sx={{
                              borderRadius: 999,
                              ...(!isWarning && {
                                borderColor: alpha(theme.palette.primary.light, 0.35),
                                color: theme.palette.text.primary,
                              }),
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              height: 24,
                              '& .MuiChip-label': {
                                px: 0.75
                              }
                            }}
                          />
                          
                          {/* 쇼트수 */}
                          <Chip
                            icon={<Glyph icon="counter_1" size="0.95rem" />}
                            label={formatLifeDisplay(mold)}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 999,
                              borderColor: alpha(theme.palette.info.light, 0.35),
                              color: theme.palette.text.primary,
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              height: 24,
                              '& .MuiChip-label': {
                                px: 0.75
                              }
                            }}
                          />
                          
                          {/* 이번출고후쇼트 (사용중일 때만) */}
                          {mold.status === 'in-use' && mold.id && postOutboundProduction.has(mold.id) && (
                            <Chip
                              icon={<Glyph icon="manufacturing" size="0.95rem" />}
                              label={`이번출고후: ${formatNumber(postOutboundProduction.get(mold.id) || 0)}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: 999,
                                borderColor: alpha(theme.palette.success.light, 0.35),
                                color: theme.palette.success.main,
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 0.75
                                }
                              }}
                            />
                          )}
                          
                          {/* 상태 */}
                          <Chip
                            label={
                              mold.status === 'in-use' && mold.id && outboundInfo.get(mold.id)
                                ? `${MoldStatusMap[mold.status as MoldStatus]} (${outboundInfo.get(mold.id)?.workType})`
                                : MoldStatusMap[(mold.status as MoldStatus) || 'stored']
                            }
                            size="small"
                            sx={{ 
                              ...getStatusStyle((mold.status as MoldStatus) || 'stored'),
                              fontSize: '0.8rem',
                              height: 24,
                              '& .MuiChip-label': {
                                px: 0.75
                              }
                            }}
                          />
                          
                          {/* 점검필요 */}
                          {isCritical && (
                            <Chip
                              icon={<Glyph icon="priority_high" size="0.95rem" />}
                              label="점검필요"
                              color="warning"
                              size="small"
                              sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.8rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 0.75
                                }
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* 생산량버튼 30% */}
                        <Box sx={{ flex: '0 0 30%', display: 'flex', alignItems: 'flex-start' }}>
                          {!isCustomerUser && mold.status === 'in-use' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => handleOpenProdLog(mold, e)}
                              fullWidth
                              sx={{
                                borderRadius: 2,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                height: 36,
                                borderColor: alpha(theme.palette.primary.light, 0.5),
                                '&:hover': {
                                  borderColor: theme.palette.primary.light,
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                }
                              }}
                            >
                              생산량
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {/* 등록 버튼: 관리자/일반 사용자만 표시 */}
      {!isCustomerUser && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: { xs: 24, md: 24 },
            right: { xs: 24, md: 32 },
            width: { xs: 64, md: 60 },
            height: { xs: 64, md: 60 },
            boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.4)}`,
          }}
          onClick={() => navigate('/molds/new')}
        >
          <AddIcon sx={{ fontSize: { xs: 28, md: 26 } }} />
        </Fab>
      )}

      {/* 생산량 입력 모달 */}
      {selectedMold && (
        <ProductionLogModal
          open={prodLogModalOpen}
          onClose={() => setProdLogModalOpen(false)}
          moldId={selectedMold.id!}
          moldNumber={selectedMold.moldNumber}
          currentShots={selectedMold.currentShots}
          cavities={selectedMold.cavities}
          onSuccess={handleProdLogSuccess}
        />
      )}
    </Box>
  );
};

export default MoldList;
